// RaceTracks - Owns track list, play index management, and ranking computation.
// Created and owned by Race. Extracts track management and play index updates
// from Sail and TrackAnimator into a cohesive class.

import * as geom from '../utils/geometry-util.js';
import * as mapDebug from "../utils/map-debug.js";
import * as util from "../utils/utils.js";
import { along, lineString } from '@turf/turf';

export class RaceTracks {

  constructor(sail, info) {
    this._sail = sail;
    this._info = info;
    this._tracks = sail.getTracks();
    this._vesselTrack = this._tracks.length > 0 ? this._tracks[0] : null;
    this._rankingsByFrame = null;
    this._handicapIdx = 0;
    this.hasRaceRankings = false;
  }

  getTracks() {
    return this._tracks;
  }

  getVesselTrack() {
    return this._vesselTrack;
  }

  calcRacePositions() {
  
    
    for (const t of this._tracks) {
      for(const f of t.getFrames()) {
        this._sail.setFrameLegId(f, null);
        this._sail.setFrameProgressM(f, null);
        this._sail.setFrameTimeDelta(f, null);
      }
    }
    this._info.updateCourseLegs();
    this.courseLegs = this._info.getCourseLegs();

    if (!this.courseLegs || this.courseLegs.length < 2) return;

    const startTs = this._info.getCourseStartTime()?.unix();
    if (!startTs) return;

    const vesselFrames = this._vesselTrack.getFrames();
    const endTs = vesselFrames[vesselFrames.length - 1].t.unix();
    const skipFactor = this._calculateSkipFactor(endTs - startTs);

    for (let i=0;i<this._tracks.length;i++) {
      this.storeRaceProgress(this._tracks[i], this.courseLegs, startTs, skipFactor);
    }
    
    for (let i=0;i<vesselFrames.length;i++) {
      this._sail.setFrameTimeDelta(vesselFrames[i], 0); 
    }

    for (let j=1;j<this._tracks.length;j++) {
      console.log("Calc relative times for:",this._tracks[j].getShipName());
      this.storeTimeDeltaRelativeToVessel(this._tracks[j], this._vesselTrack, startTs, skipFactor);
    }

    this.initRaceTimes();
    this.hasRaceRankings = true;
  }

  initRaceTimes() {
    this._startTimeCacheSec = null;
    this._finishTimeCacheSec = null;
    
    const startTime = this._info.getCourseStartTime();
    if (startTime) {
      this._startTimeCacheSec = startTime.unix();
    }

    const firstFinisher = this._tracks.reduce((earliest, track) => {
      const finishFrame = track.getRaceFinishedFrame();
      if (finishFrame == null) return earliest;
      if (earliest == null) return finishFrame;
      return finishFrame.t.unix() < earliest.t.unix() ? finishFrame : earliest;
    }, null);

    this._finishTimeCacheSec = firstFinisher?.t.unix();

  }

  //user: simple calc that skips 24 seconds when there is 24 hrs of elapsed 
  _calculateSkipFactor(elapsedSeconds) {
    const skip = Math.floor(elapsedSeconds / (60*60))
    console.log("PROGRESS CALC Skip Factor:",skip)
    return skip;
  }

  storeRaceProgress(track, courseLegs, startTs, skipFactor) {
    const frames = track.getFrames();
    
    const startIdx = track.getFrameIndexAtTs(startTs);
    console.log(track.getShipName(),"@",frames[startIdx].t.format('DD-MM HH:mm:ss'), " race start");
    for (let i = 0; i < startIdx; i++) {
      this._sail.setFrameLegId(frames[i], 'S');
      this._sail.setFrameProgressM(frames[i], 0);
      this._sail.setFrameTimeDelta(frames[i], 0);
    }
    
    let currentLegIdx = 0;
    let prevLegsProgress = 0;
    let raceProgress = 0;
    let i = startIdx;
    let prevFrame = frames[startIdx];
    let leg = courseLegs[currentLegIdx];
    while (i < frames.length && currentLegIdx < courseLegs.length) {
      const frame = frames[i];
      
      //const crossing = geom.checkCrossed(prevFrame, frame, leg.boundary);
      const crossing = this.crossedToNextLeg(prevFrame, frame, leg);
      prevFrame = frame;
      if (crossing) {
        console.log(track.getShipName(),"@",frame.t.format('DD-MM HH:mm:ss'), " enter leg:", currentLegIdx, " progress:", raceProgress);
        currentLegIdx++;
        prevLegsProgress += leg.length;
        raceProgress = Math.floor(prevLegsProgress);

        if (currentLegIdx < courseLegs.length)
          leg = courseLegs[currentLegIdx];
      } else if (i % skipFactor === 0) {
        raceProgress = Math.floor(prevLegsProgress + this.calcRaceProgress(frame, leg));
      }
      
      this._sail.setFrameProgressM(frame, raceProgress);
      this._sail.setFrameLegId(frame, currentLegIdx+1);
      
      i++;
    }
    for (let j = i; j < frames.length; j++) {
      // this._sail.setFrameLegId(frames[j], lastLeg);
      this._sail.setFrameLegId(frames[j], 'F');
      this._sail.setFrameProgressM(frames[j], raceProgress);
    }
  }

  crossedToNextLeg(prevFrame, frame, leg) {
    if (geom.checkCrossed(prevFrame, frame, leg.boundary)) 
      return true;
    if (leg.radius != null && geom.checkExitedCircle(prevFrame, frame, leg.end, leg.radius))
      return true;
  }

  calcRaceProgress(f, currentLeg) {
    const currentPos = { 'lon': f.lon, 'lat': f.lat };
    const distanceFromStart = geom.distanceM(currentLeg.start, currentPos);
    const vesselBearing = geom.bearingBetweenPoints(currentLeg.start, currentPos);
    const vectorProgress = geom.projectVector(currentLeg.bearing, distanceFromStart, vesselBearing);
    
    // Various approaches tried here, simplest is the one that remains. 
    // Code left as a documentation of this experiment

    // Calculate perpendicular distance (off-course amount)
    // const perpDistance = geom.perpendicularDistanceToLine(currentPos, currentLeg.start, currentLeg.end);
    // Subtract perpendicular distance from progress (vessel must recover this distance)
    //const adjustedProgress = vectorProgress - perpDistance;
    
    //progress constrained to leg
    const legProgress = Math.max(0,Math.min(currentLeg.length, vectorProgress));
    
    // progress is < 0 before and < leg after
    // const legProgress = currentLeg.length >= vectorProgress ? vectorProgress : 2 * currentLeg.length - vectorProgress;
    // prog < 0 before but cannot progress beyond leg
    // const legProgress = Math.min(adjustedProgress, currentLeg.length); 
    
    return legProgress;
  }

  
    /**
   * Calculate time delta for each track frame relative to vessel progress.
   * 
   * Algorithm:
   *  1. Initialize: find start indices for both tracks
   *  2. Main loop: for each track frame
   *     cases:
   *     - Normal: both progressing, find equal track frame for vessel progress, 
   *         delta = time diff between frames
   *     - Case: can't find equal progress (track maxed out)
   *        use old delta += time increments
   */
   
  storeTimeDeltaRelativeToVessel(track, vesselTrack, startTs, skipFactor) {
    let trackIdx = track.getFrameIndexAtTs(startTs);
    const trackFrames = track.getFrames();
    let vesselIdx = vesselTrack.getFrameIndexAtTs(startTs);
    const vesselFrames = vesselTrack.getFrames();

    for (let i=0;i<trackIdx;i++) {
      this._sail.setFrameTimeDelta(trackFrames[i], 0);
    }
    const trackMaxProgM = track.getMaxProgress();
    let delta = null;

    while(trackIdx < trackFrames.length) {
      const vesselProgressM = this._sail.frameProgressM(vesselFrames[vesselIdx]);

      // if we can find a point of equal progress then search
      // otherwise use the last delta.
      if (trackMaxProgM >= vesselProgressM) {
        if (trackIdx % skipFactor === 0) {
          let equalTrackIdx = trackIdx;
          while(vesselProgressM > this._sail.frameProgressM(trackFrames[equalTrackIdx]) 
            && equalTrackIdx < trackFrames.length - 1)
            equalTrackIdx++;
          while(vesselProgressM < this._sail.frameProgressM(trackFrames[equalTrackIdx]) 
            && equalTrackIdx > 0)
            equalTrackIdx--;

          const trackFrameEqual = trackFrames[equalTrackIdx];
          delta = trackFrameEqual.t.unix() - vesselFrames[vesselIdx].t.unix();

          if (trackIdx % (60*30) == 0)
            console.log(">",track.getShipName(),"@",util.formatDate(trackFrameEqual.t), " progress ", this._sail.frameProgressM(trackFrameEqual));
        }
      }
      
      this._sail.setFrameTimeDelta(trackFrames[trackIdx], delta);
      
      if (vesselIdx < vesselFrames.length - 1) {
        vesselIdx++;
      }
      trackIdx++;
    }

  }

  setHandicaps(settings, handicapIdx) {
    this._handicapIdx = handicapIdx;
    this._settings = settings;
    
    for (const track of this._tracks) {
      const raceVessel = track.isTypeVessel() 
        ? settings.getDefaultRaceVessel() 
        : settings.getRaceVesselByMMSI(track.getMMSI());
      
      if (raceVessel) {
        track.setHandicap(raceVessel.getHandicap(handicapIdx));
      } else {
        if (handicapIdx == 0)
          track.setHandicap(1);
        else
          track.setHandicap(null);
      }
    }
  }

  getCurrentRaceRankings() {
    if (!this.hasRaceRankings)
      return null;

    if (this._startTimeCacheSec == null || this._finishTimeCacheSec == null) {
      console.error("Times were null:",this._startTimeCacheSec,this._finishTimeCacheSec);
      return null;
    }
    const rankings = [];
    for (const track of this._tracks) {
      let rrFrame = track.getRaceRankingFrame();
      const frame = rrFrame != null ? rrFrame : track.getFrames()[track.getFrames().length-1];
      const legId = this._sail.frameLegId(frame);
      //this.debugDrawDeltaPoint(track, frame, legId);

      // if finished or there is still data then find corrected time using delta
      if (legId == 'F' || track.getPlayTsSec() < track.getPlayFrame().t.unix() + 10) {
        const correctedTimeSec = rrFrame == null ? null : this.findCorrectedTimeSec(track, frame);
        rankings.push({ 
          track, 
          correctedTimeSec: correctedTimeSec, 
          delta: null, 
          legId });
      } else {
        rankings.push({ 
          track, 
          correctedTimeSec: null, 
          delta: null, 
          legId: null });
      }
    }
    let minCorrectedTimeSec = 0;
    minCorrectedTimeSec = rankings.reduce((min, r) => {
      if (r.correctedTimeSec === null) return min;
      if (r.legId == 'S') return min;
      return min === null ? r.correctedTimeSec : Math.min(min, r.correctedTimeSec);
    }, null);

    for (const ranking of rankings) {
      if (ranking.correctedTimeSec !== null && minCorrectedTimeSec !== null) {
        ranking.delta = Math.floor(ranking.correctedTimeSec - minCorrectedTimeSec);
      }
    }

    rankings.sort((a, b) => {
      if (a.correctedTimeSec === null) return 1;
      if (b.correctedTimeSec === null) return -1;
      return a.correctedTimeSec - b.correctedTimeSec;
    });

    return rankings;
  }

  allBoatsFinished() {
    let didAnyFinish = false;
    for (const track of this._tracks) {
      const didFinish = track.didFinishRace();
      didAnyFinish = didAnyFinish || didFinish;
      if (didFinish && track.getPlayIndex() < track.getRaceFinishedIdx())
        return false;
    }
    return didAnyFinish;
  }

  getFinishResults() {
    const results = [];
    for (const track of this._tracks) {
      if (track.didFinishRace()) {
        const finishFrame = track.getRaceFinishedFrame();
        if (finishFrame == null) continue;
        const correctedSec = this.findCorrectedTimeSec(track, finishFrame);
        results.push({ track, finishTime: finishFrame.t, correctedSec });
      } else {
        results.push({ track, finishTime: null, correctedSec: null });
      }
    }
    results.sort((a, b) => {
      if (a.correctedSec === null) return 1;
      if (b.correctedSec === null) return -1;
      return a.correctedSec - b.correctedSec;
    });
    // results.forEach((r, i) => { r.position = i + 1; });
    return results;
  }

  findCorrectedTimeSec(track, frame) {
    const handicap = track.getHandicap();
    if (handicap !== null && handicap !== undefined) {
      const delta = this._sail.frameTimeDelta(frame);
      const elapsedSec = (frame.t.unix() - this._startTimeCacheSec) + delta;
      return elapsedSec * handicap;
    }
    return null;
  }

  debugDrawDeltaPoint(track, frame, legId) {
    const frames = track.getFrames();
    const delta = this._sail.frameTimeDelta(frame);
    let deltaFrame = frame;
    if (delta != 0)
      deltaFrame = frames[track.getFrameIndexAtTs(frame.t.unix()+delta)];

    const deltaProgress = Math.floor(this._sail.frameProgressM(deltaFrame));
    mapDebug.drawPoint(deltaFrame, { color: track.getColor(), radius: 3 });
    mapDebug.drawText(deltaFrame, deltaProgress, { color: '#ffffff', radius: 3 });
    const progress = Math.floor(this._sail.frameProgressM(frame));
    if (delta != 0) {
      mapDebug.drawText(frame, progress, { color: '#ffffff', radius: 3 });
    }
    
    const leg = this.courseLegs[legId-1];
    if (leg === undefined || leg == null) {
      console.log("leg invalid:",leg);
      return;
    }
    
    // Draw course leg line
    mapDebug.drawLine([leg.start, leg.end]);
    
    // Draw boundary lines (crossing detection zones)
    const boundary = leg.boundary;
    if (boundary && boundary.length === 2) {
      const lineLength = 200;  // Fixed 200 meters
      const line = lineString([[boundary[0].lon, boundary[0].lat], [boundary[1].lon, boundary[1].lat]]);
      const endPoint = along(line, lineLength / 1000, { units: 'kilometers' });
      mapDebug.drawLine([boundary[0], { lat: endPoint.geometry.coordinates[1], lon: endPoint.geometry.coordinates[0] }], { color: '#888888', width: 2 });
    }
    
    // Draw leg start and end caps
    const legBearing = geom.bearingBetweenPoints(leg.start, leg.end);
    const startCap1 = geom.pointAtBearingDistance(leg.start, legBearing + geom.bearingDelta(0, 90), 500);
    const startCap2 = geom.pointAtBearingDistance(leg.start, legBearing + geom.bearingDelta(0, 270), 500);
    mapDebug.drawLine([startCap1, startCap2]);
    const endCap1 = geom.pointAtBearingDistance(leg.end, legBearing + geom.bearingDelta(0, 90), 500);
    const endCap2 = geom.pointAtBearingDistance(leg.end, legBearing + geom.bearingDelta(0, 270), 500);
    mapDebug.drawLine([endCap1, endCap2]);
    
    const frameT = util.toISO8601(frame.t);
    const deltaFrameT = deltaFrame == null ? "--" : util.toISO8601(deltaFrame.t);
    console.log(track.getShipName(),"Now:",frameT, progress, delta, "Delta:",deltaFrameT, deltaProgress);
  }

}
